<template>
  <v-card flat>
    <v-form ref="form" v-model="valid" lazy-validation v-if="application">
      <h1>{{ $t("application.instructor.review.title") }}</h1>

      <p>
        {{ $t("application.instructor.review.info") }}
        (
        <v-icon x-small>$sl-navigate-left</v-icon>
        )
        {{ $t("application.review.info2") }}
      </p>
      <h4>{{ $t("application.instructor.review.person-info") }}</h4>
      <div class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.personalnumber") }}</div>
          <p>{{ application.personalNumber }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '2')">$sl-navigate-left</v-icon>
      </div>

      <div class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.mobilephone") }}</div>
          <p>{{ application.mobilephone }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '3')">$sl-navigate-left</v-icon>
      </div>

      <div class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.email") }}</div>
          <p>{{ application.email }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '4')">$sl-navigate-left</v-icon>
      </div>

      <!-- Apprentice specifics -->

      <h4>{{ $t("application.instructor.review.apprentice-info") }}</h4>
      <div class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.profession") }}</div>
          <p>{{ getProfessionNameById(application.professionId) }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '5')">$sl-navigate-left</v-icon>
      </div>

      <div
        v-if="
          application &&
          application.professionOrientations &&
          application.professionOrientations.length > 0
        "
        class="title-icon hover top"
      >
        <div>
          <div class="overline">{{ $t("fields.profession-orientation") }}</div>
          <div v-for="item in application.professionOrientations" :key="item.id">
            {{ item.professionMachineOrientation.machine.name }}
          </div>
        </div>
        <v-icon @click.stop="$emit('goToStep', '6')">$sl-navigate-left</v-icon>
      </div>

      <div class="title-icon hover top mt-4">
        <div>
          <div class="overline">{{ $t("fields.workplace") }}</div>
          <p>{{ get(selectedApplication, 'workplace.postalAddress1', '') }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '7')">$sl-navigate-left</v-icon>
      </div>

      <div class="title-icon hover top mt-4" v-if="hasDepartments($route.params.id)">
        <div>
          <div class="overline">{{ $t("fields.department") }}</div>
          <p v-if="application.department && application.department.name">
            {{ application.department.name }}
          </p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '8')">$sl-navigate-left</v-icon>
      </div>

      <div v-if="instructor && instructor.employed" class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.instructor") }}</div>
          <p>{{ instructor.employed | fullName }}</p>
        </div>

        <v-icon @click.stop="$emit('goToStep', '9')">$sl-navigate-left</v-icon>
      </div>

      <div
        v-if="application && application.employmentTypeId"
        class="title-icon hover top"
      >
        <div>
          <div class="overline">{{ $t("fields.emloymentform") }}</div>
          <p>{{ getEmploymentTypeNameById(Number(application.employmentTypeId)) }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '10')">$sl-navigate-left</v-icon>
      </div>

      <div v-if="application && application.employmentDate" class="title-icon hover top">
        <div>
          <div class="overline">{{ $t("fields.start-date") }}</div>
          <p>{{ application.employmentDate | date }}</p>
        </div>
        <v-icon @click.stop="$emit('goToStep', '10')">$sl-navigate-left</v-icon>
      </div>

      <!-- Application message -->
      <div v-if="application && application.notification && application.notification.notification.length > 0">
        <div class="title-icon">
          <h4>{{ $t('application.review.messageTitle') }}</h4>
          <v-icon @click.stop="$emit('goToStep', '11')">$sl-navigate-left</v-icon>
        </div>
        <v-textarea
          outlined
          v-model="application.notification.notification"
          :value="application.notification.notification"
        ></v-textarea>
      </div>

      <h4>{{ $t("application.instructor.review.CheckBoxTitle") }}</h4>
      <div class="d-flex mt-5">
        <v-checkbox class="mt-0 marked-blue label-top" v-model="application.sign" :label="$t('application.instructor.review.acceptCheckBox')"></v-checkbox>
      </div>
    </v-form>

    <NextButton
      class="mt-4"
      @click="nextStep"
      actionIcon="$sl-save"
      :isDisabled="application.sign != true"
    >
      {{ $t("labels.sign-application2") }}
    </NextButton>
    <BottomNav @clicked="$emit('goToStep', '-1')" />
  </v-card>
</template>

<script>
// Entryform for unknown user to become a apprentice
import API from "@/services/api.js";
import { mapActions, mapGetters } from "vuex";
import pick from "lodash/pick";
import { RulesMixin, CompanyRoles } from "@/components/mixins";
import NextButton from "@/components/Common/NextButton";
import BottomNav from "@/components/Application/BottomNav";
import get from "lodash/get";

// @group Application-Company
// ApplicationReview component
export default {
  name: "ApplicationReview",

  mixins: [RulesMixin, CompanyRoles],

  components: {
    NextButton,
    BottomNav,
  },

  data: () => ({
    application: null,
    instructor: {},
    fields: [
      "id",
      "country",
      "sign",
      "isCompanyApplication",
      "companyId",
      "step",
      "stateId",
      "createdByPersonalNumber",
      "personalNumber",
      "mobilephone",
      "email",
      'notification',
      "professionId",
      "workplaceId",
      "department",
      "instructorId",
      "employmentDate",
      "employmentTypeId",
      "professionOrientations",
    ],
    valid: false,
  }),

  created() {
    /**
     * @vuese
     * Used to call initApplication function
     */
    this.initApplication();
  },

  watch: {
    /**
     * @vuese
     * Used to watch the selectedApplication , reset validation  and call initApplication function
     */
    selectedApplication: {
      handler: function () {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        this.initApplication();
      },
      deep: true,
    },
    /**
     * @vuese
     * Used to watch the currentStep and call initApplication
     * @arg called with no argument
     */
    currentStep: {
      handler: function () {
        this.initApplication();
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters("application", ["currentStep", "selectedApplication"]),
    ...mapGetters("currentuser", ["currentUser"]),
    ...mapGetters("basic", [
      "basicProfessions",
      "getProfessionNameById",
      "getEmploymentTypeNameById",
      "basicEmploymentTypes",
    ]),
  },

  methods: {
    get,

    ...mapActions("application", ["setSelectedApplication", "updateSelectedApplication"]),
    /**
     * @vuese
     * Used to Initialize application with  selected values from all the previous steps
     */
    initApplication() {
      if (this.selectedApplication && this.selectedApplication.id) {
        this.application = pick(this.selectedApplication, this.fields);
      } else {
        this.application = Object.fromEntries(
          this.fields.map((f) => {
            return [f, null];
          })
        );
      }
      this.application.step = this.currentStep;
      this.fetchinstructor();
    },
    /**
     * @vuese
     * Used to fetch instructor and instructors
     * @arg The argument is an integer value representing company id
     */
    fetchinstructor() {
      if ( !get(this, 'application.id', -1) > 0 || !get(this, 'application.companyId', -1) > 0 ) return
      API.listEmployments(this.application.companyId)
        .then((res) => this.instructor = get(res,'data', []).find((x) => x.id === this.application.instructorId) || {})
        .catch((err) => console.log(err))
    },

    /**
     * @vuese
     * Used to go to the next step if form is valid and update the application with new criterias
     * @arg The argument is an integer value representing company id
     */
    async nextStep() {
      if (this.application.id && this.application.id > 0) {
        API.updateApplication(this.application.id, { data: this.application })
          .then(() => {
            /**
             * @vuese
             * Used to store new criterias in vuex store
             * @arg The argument is the application with all the fields fill in
             */
            this.updateSelectedApplication(this.application);
            // Fire +1
            // @arg The argument is a integer representing (move one step forward)
            this.$emit("goToStep", "+1");
          })
          .catch((error) => {
            this.$events.fire("showError", this.$t("errors.save-failure"));
          });
      }
    },
  },
};
</script>

<style scoped></style>
